import {DomainStore} from 'shared/store';
import {observable, observe, action, computed} from 'mobx';
import {endpoints} from 'shared/core';
import _ from 'lodash';

class EmployeeSelectorState {
  store = new DomainStore();
  onChange;

  @observable employees;
  @observable locations;
  @observable departments;
  @observable errors = {};

  @observable selectedEmployees = [];
  @observable defaultEmployees = [];
  @observable previouslySelectedEmployees = [];
  @observable selectionType = 'all_employees';
  @observable noEmployeesOption = false;

  receiveProps({onChange, selectedEmployees, noEmployeesOption}) {
    this.onChange = onChange;
    this.defaultEmployees = selectedEmployees || [];
    this.noEmployeesOption = noEmployeesOption;
  }

  @action async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL,
      endpoints.LOCATIONS.ALL,
      endpoints.DEPARTMENTS
    ]);

    this.employees = this.store.getEmployees();
    this.locations = this.store.getLocations();
    this.departments = this.store.getDepartments();
    this.initSelectedEmployees();

    observe(this, 'uniqueSelectedEmployees', () => this.onChange(this.uniqueSelectedEmployees), true);
  }

  @action initSelectedEmployees() {
    if (this.defaultEmployees.length > 0) {
      const employeeIds = _.map(this.defaultEmployees, 'id');
      this.selectedEmployees = _.filter(this.employees, e => _.includes(employeeIds, e.id));
    } else if (!this.noEmployeesOption) {
      this.selectedEmployees = this.employees;
    }

    if (this.selectedEmployees.length === this.employees.length) {
      this.selectionType = 'all_employees';
    } else if (this.selectedEmployees.length === 0 && this.noEmployeesOption) {
      this.selectionType = 'no_employees';
    } else {
      this.selectionType = 'select_employees';
    }
  }

  @action changeSelectionType(selectionType) {
    this.selectionType = selectionType;
    switch (selectionType) {
      case 'all_employees':
        this.previouslySelectedEmployees = this.selectedEmployees;
        this.selectedEmployees = this.employees;
        break;
      case 'select_employees':
        this.selectedEmployees = this.previouslySelectedEmployees;
        break;
      case 'no_employees':
        this.previouslySelectedEmployees = this.selectedEmployees;
        this.selectedEmployees = [];
        break;
      default:
        throw new Error(`Unknown selection type ${selectionType}`);
    }
  }

  @action selectLocation(location) {
    this.selectedEmployees = this.selectedEmployees.concat(
      this.employeesForLocation(location)
    );
  }

  @action selectDepartment(department) {
    this.selectedEmployees = this.selectedEmployees.concat(
      this.employeesForDepartment(department)
    );
  }

  @action selectManager(manager) {
    this.selectedEmployees = this.selectedEmployees.concat(
      this.employeesForManager(manager)
    );
  }

  @action selectEmployee(employee) {
    this.selectedEmployees.push(employee);
  }

  @action removeEmployee(employee) {
    this.selectedEmployees = _.reject(this.selectedEmployees, { id: employee.id });
  }

  @action clearEmployees() {
    this.selectedEmployees = [];
  }

  @computed get uniqueSelectedEmployees() {
    return _.uniqBy(this.selectedEmployees, 'id');
  }

  @computed get managers() {
    const managerIds = _.chain(this.employees).map('managerId').uniq().value();
    return _.filter(this.employees, e => managerIds.includes(e.id));
  }

  employeesForLocation(location) {
    return _.filter(this.employees, { location: { id: location.id }});
  }

  employeesForDepartment(department) {
    return _.filter(this.employees, { department: { id: department.id }});
  }

  employeesForManager(manager) {
    return _.filter(this.employees, { managerId: manager.id });
  }
}

export default EmployeeSelectorState;
